import type { ComponentPublicInstance, Ref } from 'vue';
import type { FlattenedItem, NestedSelectMenuItemBase } from './types';
import { callOrReturn } from '#layers/base/public';
import { getOutsideEventTarget } from '#layers/ui/utils/outside-interaction';

export function getChildren<TItem extends NestedSelectMenuItemBase>(item: TItem): TItem[] {
	if (!('children' in item) || !item.children) return [];
	return callOrReturn(item.children) as TItem[];
}

export function hasChildren(item: NestedSelectMenuItemBase): boolean {
	return 'children' in item && Boolean(item.children);
}

export function setExpandedItemRef<TItem extends NestedSelectMenuItemBase>(
	expandedItem: Ref<TItem | null>,
	expandedItemRef: Ref<HTMLElement | null>,
	el: Element | ComponentPublicInstance | null,
	item: TItem,
) {
	if (expandedItem.value?.id === item.id && el) {
		expandedItemRef.value = '$el' in el
			? el.$el as HTMLElement
			: el as HTMLElement;
	}
}

export function preventOutsideDismissWithinMenu(
	event: Event,
	isTargetWithinMenu: (target: EventTarget | null) => boolean,
) {
	if (isTargetWithinMenu(getOutsideEventTarget(event))) {
		event.preventDefault();
	}
}

export function stopInputBlurDismissWithinMenu(
	event: FocusEvent,
	isTargetWithinMenu: (target: EventTarget | null) => boolean,
) {
	if (isTargetWithinMenu(event.relatedTarget)) {
		event.stopImmediatePropagation();
	}
}

export function prioritizeTopLevelMatches<T>(items: T[], isTopLevel: (item: T) => boolean): T[] {
	const topLevel: T[] = [];
	const nested: T[] = [];

	for (const item of items) {
		if (isTopLevel(item)) {
			topLevel.push(item);
		}
		else {
			nested.push(item);
		}
	}

	if (nested.length === 0 || topLevel.length === 0) return items;

	return [...topLevel, ...nested];
}

export function flattenWithPaths<TItem extends NestedSelectMenuItemBase>(
	items: TItem[] | TItem[][],
	maxDepth: number,
	currentPath: TItem[] = [],
): FlattenedItem<TItem>[] {
	// Normalize grouped items to flat array
	const flatItems = Array.isArray(items[0]) ? (items as TItem[][]).flat() : items as TItem[];

	const result: FlattenedItem<TItem>[] = [];

	for (const item of flatItems) {
		// Prevent immediate recursion when the same item instance is reused in its own descendant tree.
		// Repeated ids are still valid for recursive schemas where each level creates a distinct item instance.
		if (currentPath.includes(item)) continue;

		const newPath = [...currentPath, item];
		const pathLabels = newPath.map((i) => i.label);
		const pathSearchTexts = newPath.map((entry) => entry.searchText ?? entry.label);

		const flattenedItem: FlattenedItem<TItem> = {
			item,
			path: newPath,
			pathIds: newPath.map((i) => i.id),
			pathLabels,
			searchText: pathSearchTexts.join(' '),
		};

		if ('children' in item && item.children) {
			if (item.selectable) {
				result.push(flattenedItem);
			}

			if (currentPath.length >= maxDepth) continue;

			try {
				const children = callOrReturn(item.children) as TItem[];

				result.push(...flattenWithPaths<TItem>(children, maxDepth, newPath));
			}
			catch (error) {
				console.error('Failed to flatten children for item:', item.id, error);
			}
		}
		else {
			result.push(flattenedItem);
		}
	}

	return result;
}
